import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, Instagram } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Reveal, RevealGroup, RevealItem } from '../components/motion/Reveal';
import Seo from '../components/Seo';

const API = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API}/api/enquiries/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          message: formData.message
        })
      });
      if (!res.ok) throw new Error('Submission failed');

      toast({
        title: "Message Sent Successfully",
        description: "We'll get back to you as soon as possible.",
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (err) {
      toast({
        title: "Message Failed",
        description: "We couldn't send your message. Please try again or reach us on WhatsApp.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Contact Our Surveyors | Versatile Geopick NIG Ltd"
        description="Speak with licensed surveyors in Lagos. Call, WhatsApp, email or visit our Ogombo office — Monday to Saturday."
      />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-stone-100 via-amber-50/30 to-stone-50 pt-24">
        <Reveal className="text-center px-6" duration={0.9}>
          <p className="text-emerald-700 font-medium tracking-wider uppercase text-sm mb-3">Get In Touch</p>
          <h1 className="text-5xl md:text-7xl font-light text-stone-800 tracking-tight">
            Let's <span className="font-semibold text-emerald-700">Connect</span>
          </h1>
          <p className="text-xl text-stone-600 max-w-2xl mx-auto mt-6">
            Ready to discuss your project? We're here to help.
          </p>
        </Reveal>
      </section>

      {/* Contact Cards */}
      <section className="py-16 bg-gradient-to-br from-stone-50 to-amber-50/20">
        <div className="max-w-6xl mx-auto px-6">
          <RevealGroup className="grid md:grid-cols-3 gap-6">
            <RevealItem>
            <Card className="border h-full border-stone-200 hover:border-emerald-200 transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 bg-emerald-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Phone className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-lg font-medium text-stone-800 mb-3">Phone</h3>
                <a 
                  href="tel:+2348034494507" 
                  className="text-emerald-700 hover:text-emerald-800 text-lg font-medium transition-colors duration-300"
                >
                  +234 803 449 4507
                </a>
              </CardContent>
            </Card>
            </RevealItem>

            <RevealItem>
            <Card className="border h-full border-stone-200 hover:border-emerald-200 transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 bg-emerald-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-lg font-medium text-stone-800 mb-3">Email</h3>
                <a 
                  href="mailto:Versatilegeopick@gmail.com" 
                  className="text-emerald-700 hover:text-emerald-800 text-lg font-medium break-all transition-colors duration-300"
                >
                  Versatilegeopick@gmail.com
                </a>
              </CardContent>
            </Card>
            </RevealItem>

            <RevealItem>
            <Card className="border h-full border-stone-200 hover:border-emerald-200 transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 bg-emerald-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Instagram className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-lg font-medium text-stone-800 mb-3">Instagram</h3>
                <a 
                  href="https://instagram.com/versatilegeopick"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-700 hover:text-emerald-800 text-lg font-medium transition-colors duration-300"
                >
                  @versatilegeopick
                </a>
              </CardContent>
            </Card>
            </RevealItem>
          </RevealGroup>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <Reveal direction="left">
              <h2 className="text-4xl font-light text-stone-800 mb-4">Send a Message</h2>
              <p className="text-stone-600 mb-10">
                Fill out the form and we'll get back to you as soon as possible.
              </p>

              <form onSubmit={handleSubmit} noValidate className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                    className="border-stone-300 focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      required
                      className="border-stone-300 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Phone
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+234 XXX XXX XXXX"
                      className="border-stone-300 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your surveying needs..."
                    required
                    rows={6}
                    className="border-stone-300 focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-6 text-lg font-normal transition-all duration-300 disabled:opacity-60"
                >
                  <Send className="mr-2 h-5 w-5" />
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </Reveal>

            {/* Office Info & Map */}
            <Reveal direction="right" className="space-y-8">
              <Card className="border border-stone-200">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-8">
                    <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-emerald-700" />
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-stone-800 mb-3">Office Location</h3>
                      <p className="text-stone-600 leading-relaxed">
                        3 Silverlake, off Tonia Emmanuel Road,<br />
                        Ogombo, Lagos State, Nigeria
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-emerald-700" />
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-stone-800 mb-3">Office Hours</h3>
                      <div className="space-y-2 text-stone-600">
                        <div className="flex justify-between">
                          <span>Monday - Friday:</span>
                          <span className="font-medium">8:00 AM - 6:00 PM</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Saturday:</span>
                          <span className="font-medium">9:00 AM - 4:00 PM</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Sunday:</span>
                          <span className="text-red-600 font-medium">Closed</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Google Map */}
              <div className="rounded-3xl overflow-hidden shadow-xl border-2 border-stone-200 h-[400px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3964.394914916697!2d3.5237!3d6.4584!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwMjcnMzAuMiJOIDPCsDMxJzI1LjMiRQ!5e0!3m2!1sen!2sng!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                ></iframe>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WhatsApp CTA */}
      <section className="py-16 bg-emerald-700">
        <Reveal className="max-w-4xl mx-auto px-6 text-center" direction="scale">
          <h3 className="text-3xl font-light text-white mb-4">
            Need immediate assistance?
          </h3>
          <p className="text-emerald-100 text-lg mb-8">
            Chat with us on WhatsApp for quick responses
          </p>
          <Button 
            size="lg"
            className="bg-white text-emerald-700 hover:bg-stone-50 px-10 py-6 text-lg font-normal shadow-xl hover:shadow-2xl transition-all duration-300"
            asChild
          >
            <a href="https://wa.me/2348034494507" target="_blank" rel="noopener noreferrer">
              <Phone className="mr-2 h-5 w-5" />
              WhatsApp Us
            </a>
          </Button>
        </Reveal>
      </section>
    </div>
  );
};

export default Contact;
